/**
 * Dilution Screen
 *
 * Computes dilution analysis: how much of gross receivables is reduced by
 * non-cash adjustments (credits, returns, rebates) rather than actual collections.
 * Outputs include rolling-average dilution ratios, S&P and Fitch volatility
 * measures, spike detection, and dynamic/floor dilution reserves.
 *
 * Data flow:
 *  - Reads `data.dilution` and `project.hasDilution` from Redux.
 *  - CalcParamsModal exposes dilution-specific parameters (horizon, rolling
 *    window size, stress factors) defined in DILUTION_FIELDS config.
 *  - "Calculate" dispatches to the Python `receivables_dilution` module.
 *  - NERB (Net Eligible Receivables Balance) column ties into the
 *    concentration limits and advance rate calculations downstream.
 */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
// -------------------------------------------------------------
import { calculateDilution, getDilutionData } from "Redux/data/actions";
import { useAnalysis, useScenarioFlag } from "Redux/data/hooks";
import DataTable from "Components/DataTable";
import SkeletonTable from "Components/Skeleton";
import CalcParamsModal, { ParamsButton } from "Components/CalcParamsModal";
import { DILUTION_FIELDS } from "Config/calcParamsConfig";

const headers = [
  { text: "Reference Date", key: "Reference Date", datatype: "date" },
  { text: "Gross Sales", key: "gross_sales", datatype: "float" },
  { text: "Gross Dilution", key: "gross_dilution", datatype: "float" },
  { text: "NERB", key: "nerb", datatype: "float" },
  { text: "Sales (Horizon)", key: "gross_sales_for_dilution_horizon_ratio", datatype: "float" },
  { text: "Sales (Ratio)", key: "gross_sales_for_dilution_ratio", datatype: "float" },
  { text: "Dilution (Last Month)", key: "dilution_during_last_month", datatype: "float" },
  { text: "Dilution Ratio", key: "dilution_ratio", datatype: "float", display: "percentage" },
  { text: "Dilution Ratio (Rolling Avg)", key: "dilution_ratio_rolling_average", datatype: "float", display: "percentage" },
  { text: "Dilution Ratio (Horizon Avg)", key: "dilution_ratio_rolling_average_horizon", datatype: "float", display: "percentage" },
  { text: "Spike", key: "spike", datatype: "float", display: "percentage" },
  { text: "S&P Dilution Volatility", key: "sp_dilution_volatility", datatype: "float", display: "percentage" },
  { text: "Dilution Horizon Ratio", key: "dilution_horizon_ratio", datatype: "float", display: "percentage" },
  { text: "Dilution Reserve (Dynamic)", key: "dilution_reserve_dynamic", datatype: "float", display: "percentage" },
  { text: "Dilution Reserve (Floor)", key: "dilution_reserve_floor", datatype: "float", display: "percentage" },
  { text: "Fitch Dilution Volatility", key: "fitch_dilution_volatility", datatype: "float", display: "percentage" },
];


const Dilution = () => {
  const dispatch = useDispatch();
  const [calculating, setCalculating] = useState(false);
  const [showParams, setShowParams] = useState(false);

  const { data: dilution, has: hasDilution } = useAnalysis("dilution", getDilutionData);
  const hasMaster = useScenarioFlag("hasMaster");

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  return (
    <div>
      {showParams && (
        <CalcParamsModal
          section="dilution"
          title="Dilution Parameters"
          fields={DILUTION_FIELDS}
          onClose={() => setShowParams(false)}
        />
      )}
      <div className="box" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button
          className={`button is-small ${calculating ? "is-loading" : ""}`}
          onClick={() => {
            setCalculating(true);
            dispatch(calculateDilution(() => setCalculating(false)));
          }}
        >
          {hasDilution ? "Recalculate" : "Calculate"} Dilution
        </button>
        <ParamsButton onClick={() => setShowParams(true)} />
      </div>
      {hasDilution && !dilution && <SkeletonTable />}
      {dilution && (
        <DataTable
          data={dilution.data}
          headers={headers}
          reference="Reference Date"
        />
      )}
    </div>
  );
};

export default Dilution;
